'use client';

import {
  type CSSProperties,
  forwardRef,
  memo,
  type ReactNode,
  type Ref,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal, flushSync } from 'react-dom';

import type { EditorOptions } from '../edit';
import {
  areOptionsEqual,
  CodeView as CodeViewClass,
  type CodeViewCoordinator,
  type CodeViewCreateEditorOptions,
  type CodeViewItem,
  type CodeViewLineSelection,
  type CodeViewOptions,
  type CodeViewRenderedItem,
  type CodeViewScrollTarget,
  type CodeViewSlotSnapshot,
  type DiffLineAnnotation,
  type DiffsEditor,
  type FileContents,
  type GetHoveredLineResult,
  type LineAnnotation,
} from '../index';
import { areManagedSnapshotsEqual } from '../utils/areManagedSnapshotsEqual';
import { useCreateEditor } from './EditContext';
import { renderDiffChildren } from './utils/renderDiffChildren';
import { renderFileChildren } from './utils/renderFileChildren';
import { useStableCallback } from './utils/useStableCallback';
import { WorkerPoolContext } from './WorkerPoolContext';

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

type CodeViewGutterUtilityGetter =
  | (() => GetHoveredLineResult<'file'> | undefined)
  | (() => GetHoveredLineResult<'diff'> | undefined);

export type CodeViewReactOptions<LAnnotation = undefined> = Omit<
  CodeViewOptions<LAnnotation>,
  'controlledSelection' | 'createEditor' | 'onSelectedLinesChange'
>;

interface CodeViewBaseProps<LAnnotation> {
  options?: CodeViewReactOptions<LAnnotation>;
  /**
   * Creation-time options passed to the nearest EditProvider factory.
   * CodeView supplies its item-specific change callback.
   */
  editorOptions?: Omit<EditorOptions<LAnnotation>, 'onChange'>;
  className?: string;
  style?: CSSProperties;
  containerRef?: Ref<HTMLDivElement>;
  disableWorkerPool?: boolean;
  selectedLines?: CodeViewLineSelection | null;
  onSelectedLinesChange?(selection: CodeViewLineSelection | null): void;
  onScroll?(scrollTop: number, viewer: CodeViewClass<LAnnotation>): void;
  /** Render a non-virtualized node at the very start of the scroll content,
   * before the first item. Always rendered; scrolls with the content. */
  renderCodeViewHeader?(): ReactNode;
  /** Render a non-virtualized node at the very end of the scroll content, after
   * the last item. Always rendered; scrolls with the content. */
  renderCodeViewFooter?(): ReactNode;
  /** Called with the owning item on every edited-document change. */
  onItemEditChange?(
    item: CodeViewItem<LAnnotation>,
    file: FileContents,
    lineAnnotations?:
      | LineAnnotation<LAnnotation>[]
      | DiffLineAnnotation<LAnnotation>[]
  ): void;
  /**
   * Called once with the final contents when an item's edit session ends
   * (edit turned off, item removed or collapsed). Not called for sessions
   * that produced no changes. Committing is user-space: make one combined
   * item write carrying the new file/fileDiff (with a fresh `cacheKey`,
   * since the contents changed) along with `edit: false`.
   */
  onItemEditComplete?(
    item: CodeViewItem<LAnnotation>,
    file: FileContents,
    lineAnnotations?:
      | LineAnnotation<LAnnotation>[]
      | DiffLineAnnotation<LAnnotation>[]
  ): void;
  renderCustomHeader?(item: CodeViewItem<LAnnotation>): ReactNode;
  renderHeaderPrefix?(item: CodeViewItem<LAnnotation>): ReactNode;
  renderHeaderFilenameSuffix?(item: CodeViewItem<LAnnotation>): ReactNode;
  renderHeaderMetadata?(item: CodeViewItem<LAnnotation>): ReactNode;
  renderAnnotation?(
    annotation: LineAnnotation<LAnnotation> | DiffLineAnnotation<LAnnotation>,
    item: CodeViewItem<LAnnotation>
  ): ReactNode;
  renderGutterUtility?(
    getHoveredLine: CodeViewGutterUtilityGetter,
    item: CodeViewItem<LAnnotation>
  ): ReactNode;
}

export interface ControlledCodeViewProps<
  LAnnotation,
> extends CodeViewBaseProps<LAnnotation> {
  items: readonly CodeViewItem<LAnnotation>[];
  initialItems?: never;
}

export interface UncontrolledCodeViewProps<
  LAnnotation,
> extends CodeViewBaseProps<LAnnotation> {
  // Seeds the imperative CodeView instance once. Later item changes should go
  // through the ref API instead of being reconciled from React props.
  initialItems?: readonly CodeViewItem<LAnnotation>[];
  items?: never;
}

export type CodeViewProps<LAnnotation = undefined> =
  | ControlledCodeViewProps<LAnnotation>
  | UncontrolledCodeViewProps<LAnnotation>;

export interface CodeViewHandle<LAnnotation> {
  addItems(items: readonly CodeViewItem<LAnnotation>[]): void;
  getItem(id: string): CodeViewItem<LAnnotation> | undefined;
  removeItem(id: string): boolean;
  updateItem(item: CodeViewItem<LAnnotation>): boolean;
  updateItemId(oldId: string, newId: string): boolean;
  scrollTo(target: CodeViewScrollTarget): void;
  setSelectedLines(selection: CodeViewLineSelection | null): void;
  getSelectedLines(): CodeViewLineSelection | null;
  clearSelectedLines(): void;
  getEditor(id: string): DiffsEditor<LAnnotation> | undefined;
  getInstance(): CodeViewClass<LAnnotation> | undefined;
}

type CodeViewComponent = <LAnnotation = undefined>(
  props: CodeViewProps<LAnnotation> & {
    ref?: React.Ref<CodeViewHandle<LAnnotation>>;
  }
) => React.JSX.Element;

type SlotPortalsComponent = <LAnnotation = undefined>(
  props: SlotPortalsProps<LAnnotation>
) => React.JSX.Element;

interface ManagedContentStore<LAnnotation> {
  getSnapshot(): CodeViewSlotSnapshot<LAnnotation> | undefined;
  publish(snapshot: CodeViewSlotSnapshot<LAnnotation> | undefined): void;
  subscribe(listener: () => void): () => void;
}

interface CachedDataRef<LAnnotation> {
  instance: CodeViewClass<LAnnotation> | undefined;
  items: readonly CodeViewItem<LAnnotation>[] | undefined;
  controlled: boolean;
  managedOptions: CodeViewOptions<LAnnotation> | undefined;
  disableFlushSync: boolean;
  slotCoordinator: CodeViewCoordinator<LAnnotation> | undefined;
}

function createDefaultCache<LAnnotation>(
  controlled: boolean
): CachedDataRef<LAnnotation> {
  return {
    instance: undefined,
    items: undefined,
    controlled,
    managedOptions: undefined,
    disableFlushSync: false,
    slotCoordinator: undefined,
  };
}

function CodeViewInner<LAnnotation = undefined>(
  props: CodeViewProps<LAnnotation>,
  ref: React.ForwardedRef<CodeViewHandle<LAnnotation>>
): React.JSX.Element {
  const {
    className,
    containerRef,
    disableWorkerPool = false,
    editorOptions,
    initialItems,
    items: controlledItems,
    onItemEditChange,
    onItemEditComplete,
    onScroll,
    onSelectedLinesChange,
    options,
    renderAnnotation,
    renderCodeViewFooter,
    renderCodeViewHeader,
    renderCustomHeader,
    renderGutterUtility,
    renderHeaderFilenameSuffix,
    renderHeaderMetadata,
    renderHeaderPrefix,
    selectedLines,
    style,
  } = props;
  const controlled = controlledItems !== undefined;
  const contextCreateEditor = useCreateEditor<LAnnotation>();
  const poolManager = useContext(WorkerPoolContext);
  const cachedDataRef = useRef<CachedDataRef<LAnnotation>>(
    createDefaultCache<LAnnotation>(controlled)
  );
  const hasCustomHeader = renderCustomHeader != null;
  const hasAnnotationRenderer = renderAnnotation != null;
  const hasGutterRenderer = renderGutterUtility != null;
  const hasHeaderRenderers =
    hasCustomHeader ||
    renderHeaderPrefix != null ||
    renderHeaderFilenameSuffix != null ||
    renderHeaderMetadata != null;
  const hasRenderers =
    hasHeaderRenderers || hasAnnotationRenderer || hasGutterRenderer;
  const hasCodeViewHeader = renderCodeViewHeader != null;
  const hasCodeViewFooter = renderCodeViewFooter != null;
  const emitSelectedLinesChange = useStableCallback(
    (selection: CodeViewLineSelection | null) => {
      onSelectedLinesChange?.(selection);
    }
  );
  const controlledSelection = selectedLines !== undefined;

  // Keep the adapter stable so provider and editor-option changes affect the
  // next item edit session without forcing CodeView to reconcile active editors.
  const createEditor = useStableCallback(
    (
      options: CodeViewCreateEditorOptions<LAnnotation>
    ): DiffsEditor<LAnnotation> => {
      if (contextCreateEditor == null) {
        throw new Error('CodeView: EditContext is not attached');
      }

      const editor = contextCreateEditor({
        ...editorOptions,
        ...options,
      });
      if (editor == null) {
        throw new Error(
          'CodeView: EditProvider.createEditor must return an editor instance'
        );
      }
      return editor;
    }
  );
  const emitItemEditChange = useStableCallback(
    (
      item: CodeViewItem<LAnnotation>,
      file: FileContents,
      lineAnnotations?:
        | LineAnnotation<LAnnotation>[]
        | DiffLineAnnotation<LAnnotation>[]
    ) => {
      onItemEditChange?.(item, file, lineAnnotations);
    }
  );
  const emitItemEditComplete = useStableCallback(
    (
      item: CodeViewItem<LAnnotation>,
      file: FileContents,
      lineAnnotations?:
        | LineAnnotation<LAnnotation>[]
        | DiffLineAnnotation<LAnnotation>[]
    ) => {
      onItemEditComplete?.(item, file, lineAnnotations);
    }
  );

  const managedOptions = useMemo(
    () =>
      createManagedCodeViewOptions({
        options,
        hasCustomHeader,
        hasGutterRenderer,
        hasCodeViewHeader,
        hasCodeViewFooter,
        onSelectedLinesChange:
          onSelectedLinesChange != null ? emitSelectedLinesChange : undefined,
        controlledSelection,
        createEditor: contextCreateEditor != null ? createEditor : undefined,
        onItemEditChange:
          onItemEditChange != null ? emitItemEditChange : undefined,
        onItemEditComplete:
          onItemEditComplete != null ? emitItemEditComplete : undefined,
      }),
    [
      contextCreateEditor,
      controlledSelection,
      createEditor,
      emitItemEditChange,
      emitItemEditComplete,
      emitSelectedLinesChange,
      hasCodeViewFooter,
      hasCodeViewHeader,
      hasCustomHeader,
      hasGutterRenderer,
      onItemEditChange,
      onItemEditComplete,
      onSelectedLinesChange,
      options,
    ]
  );

  const [slotContentStore] = useState<ManagedContentStore<LAnnotation>>(() =>
    createSlotContentStore()
  );
  const [, forceUpdate] = useState<unknown>({});

  const nodeRef = useStableCallback((node: HTMLDivElement | null) => {
    // If we have a pre-existing instance and there's no node or the node being
    // passed in is NOT the same as before, then we need to clean up and
    // garbage collect the old instance
    if (
      cachedDataRef.current.instance != null &&
      (node == null ||
        node !== cachedDataRef.current.instance.getContainerElement())
    ) {
      cachedDataRef.current.instance.cleanUp();
      slotContentStore.publish(undefined);
      cachedDataRef.current = createDefaultCache<LAnnotation>(controlled);
    }

    // If our node matches the existing node then we should not attempt to
    // setup.  This is a case that should never be possible to hit, but just in
    // case, lets make sure we don't re-setup an instance that is already setup
    // properly
    if (
      node != null &&
      node !== cachedDataRef.current.instance?.getContainerElement()
    ) {
      cachedDataRef.current.instance = new CodeViewClass<LAnnotation>(
        managedOptions,
        !disableWorkerPool ? poolManager : undefined,
        true
      );
      cachedDataRef.current.instance.setup(node);
    }

    if (typeof containerRef === 'function') {
      containerRef(node);
    } else if (containerRef != null) {
      containerRef.current = node;
    }
  });

  const onSnapshotChange = useStableCallback(
    (snapshot: CodeViewSlotSnapshot<LAnnotation> | undefined) => {
      if (cachedDataRef.current.disableFlushSync) {
        slotContentStore.publish(snapshot);
      } else {
        flushSync(() => {
          slotContentStore.publish(snapshot);
        });
      }
    }
  );

  const slotCoordinator: CodeViewCoordinator<LAnnotation> | undefined =
    useMemo(() => {
      // A coordinator is needed whenever React portals anything — per-item
      // slots and codeview header and footer
      if (
        !hasHeaderRenderers &&
        !hasAnnotationRenderer &&
        !hasGutterRenderer &&
        !hasCodeViewHeader &&
        !hasCodeViewFooter
      ) {
        return undefined;
      } else {
        return {
          hasHeaderRenderers,
          hasAnnotationRenderer,
          hasGutterRenderer,
          onSnapshotChange,
        };
      }
    }, [
      onSnapshotChange,
      hasAnnotationRenderer,
      hasGutterRenderer,
      hasHeaderRenderers,
      hasCodeViewHeader,
      hasCodeViewFooter,
    ]);

  useIsomorphicLayoutEffect(() => {
    return onScroll != null
      ? cachedDataRef.current.instance?.subscribeToScroll(onScroll)
      : undefined;
  });

  useIsomorphicLayoutEffect(() => {
    const {
      instance,
      controlled: prevControlled,
      items: prevItems,
      managedOptions: prevManagedOptions,
      slotCoordinator: prevSlotCoordinator,
    } = cachedDataRef.current;
    if (instance == null) {
      return;
    }

    try {
      cachedDataRef.current.disableFlushSync = true;
      let shouldRender = false;

      if (!areOptionsEqual(managedOptions, prevManagedOptions)) {
        cachedDataRef.current.managedOptions = managedOptions;
        instance.setOptions(managedOptions);
        shouldRender = true;
      }

      if (prevControlled !== controlled) {
        console.error(
          'CodeView: cannot switch between controlled and uncontrolled modes. Remount with a new key instead.'
        );
        return;
      }

      if (controlled) {
        if (controlledItems !== prevItems) {
          if (areItemListsEqual(prevItems, controlledItems)) {
            cachedDataRef.current.items = controlledItems;
          } else if (isAppendOnlyItemUpdate(prevItems, controlledItems)) {
            cachedDataRef.current.items = controlledItems;
            instance.addItems(controlledItems.slice(prevItems.length));
          } else {
            cachedDataRef.current.items = controlledItems;
            instance.setItems(controlledItems);
            shouldRender = true;
          }
        }
      }
      // If uncontrolled, we should only ever set items once, and just depend
      // on imperative instance changes going forward
      else if (prevItems == null) {
        const seedItems = initialItems ?? [];
        cachedDataRef.current.items = seedItems;
        if (seedItems.length > 0) {
          instance.setItems(seedItems);
          shouldRender = true;
        }
      }

      if (selectedLines !== undefined) {
        instance.setSelectedLines(selectedLines, { notify: false });
      }

      const slotPublish = instance.setSlotCoordinator(slotCoordinator);
      let forceInlinePublish = false;
      if (slotCoordinator !== prevSlotCoordinator) {
        if (slotCoordinator == null || prevSlotCoordinator == null) {
          forceInlinePublish = true;
        }
        cachedDataRef.current.slotCoordinator = slotCoordinator;
      }

      if (shouldRender || slotPublish) {
        instance.render(true);
      }

      // FIXME(amadeus): This feels kinda bad and flakey with regards to how
      // other things are working... it makes me think that we should
      // re-architect the slotCoordinator a bit, and maybe DON'T make it an
      // undefineable thing...
      if (slotPublish && slotCoordinator == null) {
        slotContentStore.publish(undefined);
      }

      if (forceInlinePublish) {
        forceUpdate({});
      }
    } finally {
      cachedDataRef.current.disableFlushSync = false;
    }
  });

  // Setup the ref handler
  useImperativeHandle(
    ref,
    (): CodeViewHandle<LAnnotation> => ({
      addItems(items) {
        const { controlled, instance } = cachedDataRef.current;
        assertUncontrolledCodeViewAction(controlled, 'addItems');
        if (instance == null) {
          console.error(
            'CodeView.addItems: no valid instance to append items with',
            items
          );
        } else {
          instance.addItems(items);
        }
      },
      getItem(id) {
        const { instance } = cachedDataRef.current;
        if (instance == null) {
          console.error('CodeView.getItem: no valid instance exists', id);
          return undefined;
        } else {
          return instance.getItem(id);
        }
      },
      removeItem(id) {
        const { controlled, instance } = cachedDataRef.current;
        assertUncontrolledCodeViewAction(controlled, 'removeItem');
        if (instance == null) {
          console.error(
            'CodeView.removeItem: no valid instance to remove item from',
            id
          );
          return false;
        }

        return instance.removeItem(id);
      },
      updateItem(item) {
        const { controlled, instance } = cachedDataRef.current;
        assertUncontrolledCodeViewAction(controlled, 'updateItem');
        if (instance == null) {
          console.error(
            'CodeView.updateItem: no valid instance to update item with',
            item
          );
          return false;
        }

        return instance.updateItem(item);
      },
      updateItemId(oldId, newId) {
        const { controlled, instance } = cachedDataRef.current;
        assertUncontrolledCodeViewAction(controlled, 'updateItemId');
        if (instance == null) {
          console.error(
            'CodeView.updateItemId: no valid instance to update item id with',
            oldId,
            newId
          );
          return false;
        }

        return instance.updateItemId(oldId, newId);
      },
      scrollTo(target) {
        const { instance } = cachedDataRef.current;
        if (instance == null) {
          console.error(
            'CodeView.scrollTo: no valid instance to scroll with',
            target
          );
        } else {
          instance.scrollTo(target);
        }
      },
      setSelectedLines(selection) {
        const { instance } = cachedDataRef.current;
        if (instance == null) {
          console.error(
            'CodeView.setSelectedLines: no valid instance to update selection with',
            selection
          );
        } else {
          instance.setSelectedLines(selection, { notify: false });
          emitSelectedLinesChange(selection);
        }
      },
      getSelectedLines() {
        const { instance } = cachedDataRef.current;
        if (instance == null) {
          console.error('CodeView.getSelectedLines: no valid instance exists');
          return null;
        } else {
          return instance.getSelectedLines();
        }
      },
      clearSelectedLines() {
        const { instance } = cachedDataRef.current;
        if (instance == null) {
          console.error(
            'CodeView.clearSelectedLines: no valid instance to update selection with'
          );
        } else {
          instance.clearSelectedLines({ notify: false });
          emitSelectedLinesChange(null);
        }
      },
      getEditor(id) {
        const { instance } = cachedDataRef.current;
        if (instance == null) {
          console.error('CodeView.getEditor: no valid instance exists', id);
          return undefined;
        }

        return instance.getEditor(id);
      },
      getInstance() {
        return cachedDataRef.current.instance;
      },
    }),
    [emitSelectedLinesChange]
  );

  return (
    <>
      <div ref={nodeRef} className={className} style={style} />
      {(hasRenderers || hasCodeViewHeader || hasCodeViewFooter) && (
        <SlotPortals<LAnnotation>
          managedContentStore={slotContentStore}
          renderCustomHeader={renderCustomHeader}
          renderHeaderPrefix={renderHeaderPrefix}
          renderHeaderFilenameSuffix={renderHeaderFilenameSuffix}
          renderHeaderMetadata={renderHeaderMetadata}
          renderAnnotation={renderAnnotation}
          renderGutterUtility={renderGutterUtility}
          renderCodeViewHeader={renderCodeViewHeader}
          renderCodeViewFooter={renderCodeViewFooter}
        />
      )}
    </>
  );
}

// React was a mistake
export const CodeView = forwardRef(CodeViewInner) as CodeViewComponent;

function isAppendOnlyItemUpdate<LAnnotation>(
  previousItems: readonly CodeViewItem<LAnnotation>[] | undefined,
  nextItems: readonly CodeViewItem<LAnnotation>[]
): previousItems is readonly CodeViewItem<LAnnotation>[] {
  if (previousItems == null || nextItems.length <= previousItems.length) {
    return false;
  }

  if (previousItems.length === 0) {
    return true;
  }

  for (let index = 0; index < previousItems.length; index++) {
    if (nextItems[index] !== previousItems[index]) {
      return false;
    }
  }

  return true;
}

function areItemListsEqual<LAnnotation>(
  previousItems: readonly CodeViewItem<LAnnotation>[] | undefined,
  nextItems: readonly CodeViewItem<LAnnotation>[]
): boolean {
  if (previousItems == null || previousItems.length !== nextItems.length) {
    return false;
  }

  for (let index = 0; index < previousItems.length; index++) {
    if (previousItems[index] !== nextItems[index]) {
      return false;
    }
  }

  return true;
}

function assertUncontrolledCodeViewAction(
  controlled: boolean,
  action: string
): void {
  if (!controlled) {
    return;
  }

  throw new Error(
    `CodeView.${action} cannot be used when CodeView is controlled. Use initialItems for imperative item updates.`
  );
}

function createSlotContentStore<
  LAnnotation,
>(): ManagedContentStore<LAnnotation> {
  let snapshot: CodeViewSlotSnapshot<LAnnotation> | undefined;
  const listeners = new Set<() => void>();

  return {
    getSnapshot() {
      return snapshot;
    },
    publish(nextSnapshot) {
      if (areManagedSnapshotsEqual(snapshot, nextSnapshot)) {
        return;
      }

      snapshot = nextSnapshot;
      for (const listener of listeners) {
        listener();
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

interface CreateManagedCodeViewOptionsProps<LAnnotation> {
  options: CodeViewReactOptions<LAnnotation> | undefined;
  hasCustomHeader: boolean;
  hasGutterRenderer: boolean;
  hasCodeViewHeader: boolean;
  hasCodeViewFooter: boolean;
  onSelectedLinesChange?(selection: CodeViewLineSelection | null): void;
  controlledSelection: boolean;
  createEditor: CodeViewOptions<LAnnotation>['createEditor'];
  onItemEditChange: CodeViewOptions<LAnnotation>['onItemEditChange'];
  onItemEditComplete: CodeViewOptions<LAnnotation>['onItemEditComplete'];
}

function createManagedCodeViewOptions<LAnnotation>({
  options,
  hasCustomHeader,
  hasGutterRenderer,
  hasCodeViewHeader,
  hasCodeViewFooter,
  onSelectedLinesChange,
  controlledSelection,
  createEditor,
  onItemEditChange,
  onItemEditComplete,
}: CreateManagedCodeViewOptionsProps<LAnnotation>): CodeViewOptions<LAnnotation> {
  const managedOptions: CodeViewOptions<LAnnotation> = {
    ...options,
    controlledSelection,
    onSelectedLinesChange,
    createEditor,
  };

  // Prop-level editor callbacks win over their options-object counterparts,
  // but an absent prop must not clobber a value provided via `options`.
  if (onItemEditChange != null) {
    managedOptions.onItemEditChange = onItemEditChange;
  }
  if (onItemEditComplete != null) {
    managedOptions.onItemEditComplete = onItemEditComplete;
  }

  // The imperative CodeView adapters use this callback's presence to
  // switch file and diff headers into custom-slot mode. React portals
  // provide the actual header content, so this placeholder
  // intentionally returns nothing.
  if (hasCustomHeader) {
    managedOptions.renderCustomHeader = noopRender;
  }

  // The imperative CodeView adapters use this callback's presence to
  // create the custom gutter utility slot. React portals provide the
  // actual content, so this placeholder intentionally returns nothing.
  if (hasGutterRenderer) {
    managedOptions.renderGutterUtility = noopRender;
  }

  // The imperative CodeView adapters use these callbacks' presence to create the
  // header/footer host elements. React portals the actual content into them (via
  // the slot snapshot), so these placeholders intentionally return nothing.
  if (hasCodeViewHeader) {
    managedOptions.renderCodeViewHeader = noopRender;
  }
  if (hasCodeViewFooter) {
    managedOptions.renderCodeViewFooter = noopRender;
  }

  return managedOptions;
}

interface RenderCodeViewItemChildrenProps<LAnnotation> {
  renderedItem: CodeViewRenderedItem<LAnnotation>;
  renderCustomHeader: CodeViewBaseProps<LAnnotation>['renderCustomHeader'];
  renderHeaderPrefix: CodeViewBaseProps<LAnnotation>['renderHeaderPrefix'];
  renderHeaderFilenameSuffix: CodeViewBaseProps<LAnnotation>['renderHeaderFilenameSuffix'];
  renderHeaderMetadata: CodeViewBaseProps<LAnnotation>['renderHeaderMetadata'];
  renderAnnotation: CodeViewBaseProps<LAnnotation>['renderAnnotation'];
  renderGutterUtility: CodeViewBaseProps<LAnnotation>['renderGutterUtility'];
}

interface SlotPortalsProps<LAnnotation> {
  managedContentStore: ManagedContentStore<LAnnotation>;
  renderCustomHeader: CodeViewBaseProps<LAnnotation>['renderCustomHeader'];
  renderHeaderPrefix: CodeViewBaseProps<LAnnotation>['renderHeaderPrefix'];
  renderHeaderFilenameSuffix: CodeViewBaseProps<LAnnotation>['renderHeaderFilenameSuffix'];
  renderHeaderMetadata: CodeViewBaseProps<LAnnotation>['renderHeaderMetadata'];
  renderAnnotation: CodeViewBaseProps<LAnnotation>['renderAnnotation'];
  renderGutterUtility: CodeViewBaseProps<LAnnotation>['renderGutterUtility'];
  renderCodeViewHeader: CodeViewBaseProps<LAnnotation>['renderCodeViewHeader'];
  renderCodeViewFooter: CodeViewBaseProps<LAnnotation>['renderCodeViewFooter'];
}

const SlotPortals = memo(function SlotPortals<LAnnotation>({
  managedContentStore,
  renderCustomHeader,
  renderHeaderPrefix,
  renderHeaderFilenameSuffix,
  renderHeaderMetadata,
  renderAnnotation,
  renderGutterUtility,
  renderCodeViewHeader,
  renderCodeViewFooter,
}: SlotPortalsProps<LAnnotation>) {
  'use no memo';
  const subscribe = useStableCallback((listener: () => void) =>
    managedContentStore.subscribe(listener)
  );
  const getSnapshot = useStableCallback(() =>
    managedContentStore.getSnapshot()
  );
  const snapshot = useSyncExternalStore<
    CodeViewSlotSnapshot<LAnnotation> | undefined
  >(subscribe, getSnapshot, getSnapshot);
  let itemKeys = '';
  for (const item of snapshot?.items ?? []) {
    itemKeys += `${item.id}:${item.version}:${item.type}`;
  }
  // NOTE(amadeus): And just like that, the react compiler do be failing us
  // lol... we need to pre-render everything for items, headers and footers to
  // not trigger needless renders while scrolling
  const renderedItems = useMemo(
    () => {
      return snapshot?.items?.map((renderedItem) =>
        createPortal(
          renderCodeViewItemChildren({
            renderedItem,
            renderCustomHeader,
            renderHeaderPrefix,
            renderHeaderFilenameSuffix,
            renderHeaderMetadata,
            renderAnnotation,
            renderGutterUtility,
          }),
          renderedItem.element,
          renderedItem.id
        )
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      renderCustomHeader,
      renderHeaderPrefix,
      renderHeaderFilenameSuffix,
      renderHeaderMetadata,
      renderAnnotation,
      renderGutterUtility,
      itemKeys,
    ]
  );
  const renderedHeader = useMemo(() => {
    return renderCodeViewHeader != null && snapshot?.header != null
      ? createPortal(renderCodeViewHeader(), snapshot.header)
      : null;
  }, [renderCodeViewHeader, snapshot?.header]);
  const renderedFooter = useMemo(() => {
    return renderCodeViewFooter != null && snapshot?.footer != null
      ? createPortal(renderCodeViewFooter(), snapshot.footer)
      : null;
  }, [renderCodeViewFooter, snapshot?.footer]);
  return (
    <>
      {renderedItems}
      {renderedHeader}
      {renderedFooter}
    </>
  );
}) as SlotPortalsComponent;

function renderCodeViewItemChildren<LAnnotation>({
  renderedItem,
  renderCustomHeader,
  renderHeaderPrefix,
  renderHeaderFilenameSuffix,
  renderHeaderMetadata,
  renderAnnotation,
  renderGutterUtility,
}: RenderCodeViewItemChildrenProps<LAnnotation>): ReactNode {
  if (renderedItem.type === 'diff') {
    const { item, instance } = renderedItem;
    return renderDiffChildren({
      fileDiff: item.fileDiff,
      renderCustomHeader:
        renderCustomHeader != null ? () => renderCustomHeader(item) : undefined,
      renderHeaderPrefix:
        renderHeaderPrefix != null ? () => renderHeaderPrefix(item) : undefined,
      renderHeaderFilenameSuffix:
        renderHeaderFilenameSuffix != null
          ? () => renderHeaderFilenameSuffix(item)
          : undefined,
      renderHeaderMetadata:
        renderHeaderMetadata != null
          ? () => renderHeaderMetadata(item)
          : undefined,
      renderAnnotation:
        renderAnnotation != null
          ? (annotation) => renderAnnotation(annotation, item)
          : undefined,
      lineAnnotations: item.annotations,
      renderGutterUtility:
        renderGutterUtility != null
          ? (getHoveredLine) => renderGutterUtility(getHoveredLine, item)
          : undefined,
      getHoveredLine: instance.getHoveredLine,
    });
  } else {
    const { item, instance } = renderedItem;
    return renderFileChildren({
      file: item.file,
      renderCustomHeader:
        renderCustomHeader != null ? () => renderCustomHeader(item) : undefined,
      renderHeaderPrefix:
        renderHeaderPrefix != null ? () => renderHeaderPrefix(item) : undefined,
      renderHeaderFilenameSuffix:
        renderHeaderFilenameSuffix != null
          ? () => renderHeaderFilenameSuffix(item)
          : undefined,
      renderHeaderMetadata:
        renderHeaderMetadata != null
          ? () => renderHeaderMetadata(item)
          : undefined,
      renderAnnotation:
        renderAnnotation != null
          ? (annotation) => renderAnnotation(annotation, item)
          : undefined,
      lineAnnotations: item.annotations,
      renderGutterUtility:
        renderGutterUtility != null
          ? (getHoveredLine) => renderGutterUtility(getHoveredLine, item)
          : undefined,
      getHoveredLine: instance.getHoveredLine,
    });
  }
}

function noopRender() {
  return undefined;
}
